import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  message,
  Modal,
  Card,
  Avatar,
  Tooltip,
  Form,
  InputNumber,
  DatePicker,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  ReloadOutlined,
  CreditCardOutlined,
} from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import PersianDatePicker from '../../components/PersianDatePicker';
import { pettyCashApi, bankAccountsApi, expensesApi } from '../../utils/api';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import { formatMoneyFa } from '../../utils/formatPriceToman';

const { Option } = Select;

const toPersianNumber = (num) => {
  if (num === null || num === undefined) return '-';
  const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  return String(num).replace(/\d/g, (d) => persianDigits[parseInt(d)]);
};

const PettyCash = () => {
  const [loading, setLoading] = useState(false);
  const [pettyCashes, setPettyCashes] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isSpendModalVisible, setIsSpendModalVisible] = useState(false);
  const [selectedPettyCash, setSelectedPettyCash] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  
  const [createForm] = Form.useForm();
  const [spendForm] = Form.useForm();

  const fetchPettyCashes = async () => {
    setLoading(true);
    try {
      const res = await pettyCashApi.getAll();
      if (res?.data?.petty_cashes) {
        setPettyCashes(Array.isArray(res.data.petty_cashes) ? res.data.petty_cashes : []);
      } else if (res?.petty_cashes) {
        setPettyCashes(Array.isArray(res.petty_cashes) ? res.petty_cashes : []);
      } else if (Array.isArray(res?.data)) {
        setPettyCashes(Array.isArray(res.data) ? res.data : []);
      }
    } catch (error) {
      console.error('Error fetching petty cash:', error);
      message.error('خطا در دریافت لیست تنخواه‌گردان');
    } finally {
      setLoading(false);
    }
  };

  const fetchDependencies = async () => {
    try {
      const [bankRes, createRes] = await Promise.all([
        bankAccountsApi.getAll(),
        expensesApi.getCreateData(),
      ]);
      if (bankRes?.data?.accounts) setBankAccounts(bankRes.data.accounts);
      else if (bankRes?.accounts) setBankAccounts(bankRes.accounts);
      
      if (createRes?.data?.employees) setUsers(createRes.data.employees);
      else if (createRes?.employees) setUsers(createRes.employees);
      
      if (createRes?.data?.categories) setCategories(createRes.data.categories);
      else if (createRes?.categories) setCategories(createRes.categories);
    } catch (error) {
      console.error('Error fetching dependencies', error);
    }
  };

  useEffect(() => {
    fetchPettyCashes();
    fetchDependencies();
  }, []);

  const handleCreate = async (values) => {
    setActionLoading(true);
    try {
      await pettyCashApi.create({
        ...values,
        initial_amount: Number(values.initial_amount),
      });
      message.success('تنخواه جدید با موفقیت ایجاد شد');
      setIsCreateModalVisible(false);
      createForm.resetFields();
      fetchPettyCashes();
    } catch (error) {
      message.error('خطا در ایجاد تنخواه');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSpend = async (values) => {
    if (!selectedPettyCash) return;
    setActionLoading(true);
    try {
      await pettyCashApi.spend(selectedPettyCash.id, {
        ...values,
        price: Number(values.price),
        purchase_date: values.purchase_date ? values.purchase_date.format('YYYY-MM-DD') : undefined,
      });
      message.success('هزینه از محل تنخواه با موفقیت ثبت شد');
      setIsSpendModalVisible(false);
      spendForm.resetFields();
      fetchPettyCashes();
    } catch (error) {
      message.error('خطا در ثبت هزینه');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSettle = (id) => {
    Modal.confirm({
      title: <span style={{ fontFamily: 'CustomFont' }}>تسویه تنخواه</span>,
      content: (
        <span style={{ fontFamily: 'CustomFont' }}>
          آیا از تسویه این دوره تنخواه اطمینان دارید؟
        </span>
      ),
      okText: 'بله، تسویه کن',
      cancelText: 'لغو',
      onOk: async () => {
        try {
          await pettyCashApi.settle(id);
          message.success('تنخواه با موفقیت تسویه شد');
          fetchPettyCashes();
        } catch (error) {
          message.error('خطا در تسویه تنخواه');
        }
      },
    });
  };

  const columns = [
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>عنوان تنخواه</span>,
      dataIndex: 'title',
      key: 'title',
      render: (text) => <span className="font-medium" style={{ fontFamily: 'CustomFont' }}>{text}</span>,
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>مسئول تنخواه</span>,
      dataIndex: 'user',
      key: 'user',
      render: (user) => user ? <span style={{ fontFamily: 'CustomFont' }}>{user.name}</span> : '-',
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>موجودی تنخواه</span>,
      dataIndex: 'current_balance',
      key: 'current_balance',
      render: (balance, record) => (
        <span className="text-blue-600 font-medium" style={{ fontFamily: 'CustomFont' }}>
          {formatMoneyFa(balance !== undefined ? balance : record.initial_amount)}
        </span>
      ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>وضعیت</span>,
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'settled' ? 'success' : 'processing'} style={{ fontFamily: 'CustomFont' }}>
          {status === 'settled' ? 'تسویه شده' : 'در جریان'}
        </Tag>
      ),
    },
    {
      title: <span style={{ fontFamily: 'CustomFont' }}>عملیات</span>,
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            style={{ fontFamily: 'CustomFont', backgroundColor: '#1890ff' }}
            onClick={() => {
              setSelectedPettyCash(record);
              setIsSpendModalVisible(true);
            }}
            disabled={record.status === 'settled'}
          >
            ثبت هزینه
          </Button>
          <Button
            size="small"
            style={{ fontFamily: 'CustomFont' }}
            onClick={() => handleSettle(record.id)}
            disabled={record.status === 'settled'}
          >
            تسویه دوره
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          {/* Header */}
          <div className="border-b border-gray-200 dark:border-gray-700 p-6 bg-white dark:bg-gray-800 rounded-t-lg">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-orange-50 dark:bg-orange-900/20 flex items-center justify-center">
                  <CreditCardOutlined className="text-orange-600 dark:text-orange-400 text-xl" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-0" style={{ fontFamily: 'CustomFont' }}>
                    مدیریت تنخواه‌گردان
                  </h1>
                  <p className="text-gray-500 dark:text-gray-400 text-sm m-0">
                    افتتاح، ثبت هزینه و تسویه کیف‌پول‌های تنخواه
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Tooltip title="بروزرسانی">
                  <Button icon={<ReloadOutlined />} onClick={fetchPettyCashes} size="middle" loading={loading} />
                </Tooltip>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setIsCreateModalVisible(true)}
                  size="middle"
                  className="bg-green-600 hover:bg-green-700 border-0"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  افتتاح تنخواه جدید
                </Button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 bg-gray-50 dark:bg-gray-900/50">
            {loading ? (
              <TableSkeleton rows={5} columns={5} />
            ) : (
              <Card className="dark:bg-gray-800 border-0 shadow-sm">
                <Table
                  columns={columns}
                  dataSource={pettyCashes}
                  rowKey="id"
                  pagination={false}
                />
              </Card>
            )}
          </div>
        </Card>
      </div>

      {/* Modal افتتاح تنخواه */}
      <Modal
        title={<span style={{ fontFamily: 'CustomFont' }}>افتتاح تنخواه جدید</span>}
        open={isCreateModalVisible}
        onCancel={() => setIsCreateModalVisible(false)}
        footer={null}
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreate}>
          <Form.Item name="title" label={<span style={{ fontFamily: 'CustomFont' }}>عنوان تنخواه</span>} rules={[{ required: true }]}>
            <Input style={{ fontFamily: 'CustomFont' }} />
          </Form.Item>
          <Form.Item name="user_id" label={<span style={{ fontFamily: 'CustomFont' }}>مسئول تنخواه</span>} rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="children" style={{ fontFamily: 'CustomFont' }}>
              {users.map(u => <Option key={u.id} value={u.id}>{u.name}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="initial_amount" label={<span style={{ fontFamily: 'CustomFont' }}>مبلغ اولیه</span>} rules={[{ required: true }]}>
            <InputNumber className="w-full" style={{ fontFamily: 'CustomFont' }} />
          </Form.Item>
          <Form.Item name="bank_account_id" label={<span style={{ fontFamily: 'CustomFont' }}>حساب بانکی (محل تأمین)</span>} rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="children" style={{ fontFamily: 'CustomFont' }}>
              {bankAccounts.map(b => <Option key={b.id} value={b.id}>{b.account_name}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="notes" label={<span style={{ fontFamily: 'CustomFont' }}>توضیحات</span>}>
            <Input.TextArea rows={3} style={{ fontFamily: 'CustomFont' }} />
          </Form.Item>
          <Form.Item className="mb-0 text-left">
            <Space>
              <Button onClick={() => setIsCreateModalVisible(false)} style={{ fontFamily: 'CustomFont' }}>لغو</Button>
              <Button type="primary" htmlType="submit" loading={actionLoading} style={{ fontFamily: 'CustomFont', backgroundColor: '#1890ff' }}>
                ایجاد تنخواه
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal ثبت هزینه از تنخواه */}
      <Modal
        title={<span style={{ fontFamily: 'CustomFont' }}>ثبت هزینه از تنخواه</span>}
        open={isSpendModalVisible}
        onCancel={() => setIsSpendModalVisible(false)}
        footer={null}
      >
        <Form form={spendForm} layout="vertical" onFinish={handleSpend}>
          <Form.Item name="item_name" label={<span style={{ fontFamily: 'CustomFont' }}>عنوان هزینه</span>} rules={[{ required: true }]}>
            <Input style={{ fontFamily: 'CustomFont' }} />
          </Form.Item>
          <Form.Item name="price" label={<span style={{ fontFamily: 'CustomFont' }}>مبلغ</span>} rules={[{ required: true }]}>
            <InputNumber className="w-full" style={{ fontFamily: 'CustomFont' }} />
          </Form.Item>
          <Form.Item name="category_id" label={<span style={{ fontFamily: 'CustomFont' }}>دسته‌بندی</span>} rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="children" style={{ fontFamily: 'CustomFont' }}>
              {categories.map(c => <Option key={c.id} value={c.id}>{c.category_name}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="purchase_date" label={<span style={{ fontFamily: 'CustomFont' }}>تاریخ خرید</span>}>
            <PersianDatePicker placeholder="انتخاب تاریخ" style={{ fontFamily: 'CustomFont' }} />
          </Form.Item>
          <Form.Item name="description" label={<span style={{ fontFamily: 'CustomFont' }}>توضیحات</span>}>
            <Input.TextArea rows={2} style={{ fontFamily: 'CustomFont' }} />
          </Form.Item>
          <Form.Item className="mb-0 text-left">
            <Space>
              <Button onClick={() => setIsSpendModalVisible(false)} style={{ fontFamily: 'CustomFont' }}>لغو</Button>
              <Button type="primary" htmlType="submit" loading={actionLoading} style={{ fontFamily: 'CustomFont', backgroundColor: '#1890ff' }}>
                ثبت هزینه
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

    </MainLayout>
  );
};

export default PettyCash;
